/**
 * A dependency-free external store and a React-16-compatible selector hook.
 * See .claude/context/store-primitive.md
 */
import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';

// Dev-only action log, computed once. See .claude/context/store-primitive.md
// eslint-disable-next-line
const DEV_LOG = typeof import.meta !== 'undefined' && !!import.meta.env
  && import.meta.env.DEV === true && !import.meta.env.VITEST && import.meta.env.MODE !== 'test';

/**
 * @param {object} initialState
 * @returns {{ getState: () => object, setState: (patch) => void, subscribe: (fn) => (() => void) }}
 */
export const createStore = (initialState) => {
  let state = initialState;
  const listeners = new Set();

  const getState = () => state;

  // `name` only labels the DEV action log. See .claude/context/store-primitive.md
  const setState = (patch, name) => {
    const partial = typeof patch === 'function' ? patch(state) : patch;
    if (partial === state) return;
    const prev = state;
    state = { ...state, ...partial };
    if (DEV_LOG && name) {
      // eslint-disable-next-line no-console
      console.debug('[maa-store]', name, { patch: partial, prev, next: state });
    }
    listeners.forEach((fn) => fn(state));
  };

  const subscribe = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  // Selector-scoped subscription, mirrors zustand's subscribeWithSelector.
  // See .claude/context/store-primitive.md
  const subscribeWithSelector = (selector, listener, options = {}) => {
    const { equalityFn = Object.is, fireImmediately = false } = options;
    let current = selector(state);
    if (fireImmediately) listener(current, current);
    return subscribe((nextState) => {
      const next = selector(nextState);
      if (!equalityFn(current, next)) {
        const prev = current;
        current = next;
        listener(next, prev);
      }
    });
  };

  return {
    getState, setState, subscribe, subscribeWithSelector,
  };
};

/**
 * Subscribe a component to a slice of a store. The component re-renders only
 * when the selected value changes, per `isEqual` (default `Object.is`).
 *
 * @param {object} store - a createStore() instance
 * @param {(state: object) => any} selector
 * @param {(a: any, b: any) => boolean} [isEqual]
 */
export const useStore = (store, selector, isEqual = Object.is) => {
  const [selected, setSelected] = useState(() => selector(store.getState()));
  // Keep the latest selected value in a ref so the subscription closure always
  // compares against what was last rendered, without re-subscribing per render.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    const check = (state) => {
      const next = selector(state);
      if (!isEqual(selectedRef.current, next)) {
        selectedRef.current = next;
        setSelected(next);
      }
    };
    const unsubscribe = store.subscribe(check);
    // State may have changed between the initial render and this subscription.
    check(store.getState());
    return unsubscribe;
  }, [store]);

  return selected;
};

/**
 * Merge slice factories into one Zustand-style factory. The slices' objects merge
 * shallowly into one flat state. See src/store/README.md ("Slice convention").
 *
 * @param {...((set, get) => object)} sliceFactories
 * @returns {(set, get) => object}
 */
export const combineSlices = (...sliceFactories) => (set, get) => (
  sliceFactories.reduce((acc, factory) => ({ ...acc, ...factory(set, get) }), {})
);

/**
 * Build the central store from a slice factory, and return the StoreApi that
 * `useStore` selects from. See src/store/README.md ("Slice convention").
 *
 * @param {(set, get) => object} createSlices
 * @returns {{ getState, setState, subscribe, subscribeWithSelector }} the StoreApi
 */
export const createSlicedStore = (createSlices) => {
  const store = createStore({});
  store.setState(createSlices(store.setState, store.getState));
  return store;
};

/**
 * Shallow equality for `useStore`'s `isEqual`, like Zustand's `shallow`. Use it when a
 * selector returns a new object of several fields, which always fails `Object.is`.
 *
 * @param {any} a
 * @param {any} b
 * @returns {boolean}
 */
export const shallow = (objA, objB) => {
  if (Object.is(objA, objB)) return true;
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(
    (key) => Object.prototype.hasOwnProperty.call(objB, key) && Object.is(objA[key], objB[key])
  );
};

/**
 * Immer setter for nested slice state: `recipe(draft)` mutates a draft, and `set` commits
 * the immutable result. Opt-in only; see src/store/README.md ("Store primitives").
 *
 * @param {(patch, name?) => void} set - a store's setState
 * @param {() => object} get - a store's getState
 * @returns {(recipe: (draft: object) => void, name?: string) => void}
 */
export const createImmerSet = (set, get) => (recipe, name) => set(produce(get(), recipe), name);
